import { Directive, ElementRef, inject, Input, OnInit } from '@angular/core';

import { ApWindowService } from '@ap/cdk';
import { ApContentItem, ApContentService } from '@ap/content';
import { MijnWebcomponentIdentifier } from '@ap/mijn-navigatie';
import {
    ApPegaCaseService,
    ApPegaResourceExtraHeaders,
    CaseChange,
    InterpolateParam,
    LoadAction,
    PegaBusinessService,
    PegaChannelName,
    PegaFieldUpdates,
    PegaTitleService,
    TemplateType
} from '@ap/pega';

import { ApPegaEvents, DistributionChannelType } from './base-starter-app.model';

@Directive()
export abstract class BaseStarterAppDirective implements OnInit, ApPegaEvents {
    @Input() public apPegaChannel: PegaChannelName = PegaChannelName.MIJN;
    @Input() public apPegaCloseUrl?: string;
    @Input() public apPegaContent: ApContentItem = {};
    @Input() public apPegaContentQueryParams: string[] = [];
    @Input() public apPegaErrorShowCloseButton?: boolean;
    @Input() public apPegaInterpolateParams: string | InterpolateParam = {};
    @Input() public apPegaMijnBusiness?: boolean = false;
    @Input() public apPegaMijnTemplateType?: TemplateType = TemplateType.ACTION;
    @Input() public apPegaSendDistributionChannelType = false;
    @Input() public apPegaUseQueryParams: ('caseID' | 'assignmentID' | 'actionID' | 'initWithSubmit')[] = [];
    @Input() public apPegaUseState = true;

    @Input() public pegaCaseTypeId?: string;
    @Input() public pegaCaseTypeIdBusiness?: string;
    @Input() public pegaResumeCaseID?: string;
    @Input() public pegaResumeAssignmentID?: string;
    @Input() public pegaResumeActionID?: string;
    @Input() public pegaResumeInitWithSubmit?: boolean;
    @Input() public name?: string;
    /** Caution: this is sent alongside case creation (posted init params)!! */
    @Input() public pegaContent?: any = {};
    @Input() public pegaExtraHeaders?: ApPegaResourceExtraHeaders;

    @Input() public webcomponentIdentifier?: MijnWebcomponentIdentifier;

    @Input() public inputPropertiesFromAttributesBlacklist?: string[] = [];

    protected readonly pegaBusinessService = inject(PegaBusinessService);
    protected readonly apContentService = inject(ApContentService);
    protected readonly apPegaCaseService = inject(ApPegaCaseService);
    protected readonly apWindowService = inject(ApWindowService);
    protected readonly element = inject(ElementRef);
    protected readonly pegaTitleService = inject(PegaTitleService);

    protected loading = true;
    protected caseTypeID: string;
    protected abstract distributionChannelType: DistributionChannelType;

    public get starterAppTitle(): string {
        return this.pegaTitleService.starterAppTitle;
    }

    protected get isLoading(): boolean {
        return this.loading || !this.caseTypeID;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public onCaseChange(caseChange?: CaseChange): void {
        return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public onCaseTypeChange(caseTypeChange: string = ''): void {
        return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public onAssignmentNameChanged(assignmentNameChange: string = ''): void {
        return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public onLoadAction(loadAction?: LoadAction): void {
        return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public onAssignmentComplete(pegaFieldUpdates?: PegaFieldUpdates): void {
        return;
    }

    public ngOnInit(): void {
        this.pegaTitleService.setDefaultStarterAppTitle();

        this.handleElementParameters();
        this.handleQueryParameters();
        this.handleContentQueryParams();

        this.loading = false;
    }

    public getSetting(key: string): string | undefined {
        return (this.element.nativeElement as HTMLElement)?.getAttribute(key);
    }

    public getSettingJson(key: string): null | any {
        try {
            return JSON.parse(this.getSetting(key));
        } catch (_error) {
            return undefined;
        }
    }

    private setInputPropertiesFromAttributes(): void {
        const whitelist = [
            'apPegaChannel',
            'apPegaCloseUrl',
            'apPegaErrorShowCloseButton',
            'apPegaMijnBusiness',
            'apPegaMijnTemplateType',
            'apPegaSendDistributionChannelType',
            'apPegaUseState',
            'confirmBeforeExit',
            'forceNativeBackButton',
            'name',
            'pegaCaseTypeId',
            'pegaCaseTypeIdBusiness',
            'pegaExtraHeaders',
            'webcomponentIdentifier'
        ].filter((item) => !this.inputPropertiesFromAttributesBlacklist.includes(item));

        for (const key of whitelist) {
            const setting = this.getSettingJson(key) ?? this.getSetting(key);

            if (typeof this[key] === 'undefined') {
                Object.defineProperty(this, key, {
                    value: setting,
                    writable: true
                });
                continue;
            }

            this[key] = setting ?? this[key];
        }
    }

    private handleElementParameters(): void {
        this.setInputPropertiesFromAttributes();

        this.pegaContent = this.getSettingJson('pegaContent') ?? this.getSetting('pegaContent') ?? this.pegaContent;

        if (this.getSetting('apPegaContent')) {
            this.apContentService.addContentItems(this.getSettingJson('apPegaContent') || {});
        }

        if (this.getSetting('apPegaInterpolateParams')) {
            this.apPegaCaseService.addInterpolateParams(this.getSettingJson('apPegaInterpolateParams') || {});
        }

        if (this.getSetting('apPegaUseQueryParams')) {
            const addInterpolateParams = this.getSettingJson('apPegaUseQueryParams');
            if (Array.isArray(addInterpolateParams) && addInterpolateParams.length > 0) {
                this.apPegaUseQueryParams = addInterpolateParams;
            }
        }

        if (this.getSetting('apPegaContentQueryParams')) {
            const addInterpolateParams = this.getSettingJson('apPegaContentQueryParams');
            if (Array.isArray(addInterpolateParams) && addInterpolateParams.length > 0) {
                this.apPegaContentQueryParams = addInterpolateParams;
            }
        }
    }

    private getURLSearchParams(): URLSearchParams {
        return new URL(this.apWindowService.getWindow().location.href.replace('/#/', '/')).searchParams;
    }

    private handleQueryParameters(): void {
        const urlSearchParams = this.getURLSearchParams();

        const isBusiness = urlSearchParams.get('isBusiness');
        const forceIsBusiness = isBusiness === null ? null : isBusiness === 'true';
        this.setCaseTypeId(forceIsBusiness);

        if (this.apPegaUseQueryParams.length > 0) {
            if (urlSearchParams.get('caseID')) {
                this.pegaResumeCaseID = urlSearchParams.get('caseID');
            }

            if (urlSearchParams.get('assignmentID')) {
                this.pegaResumeAssignmentID = urlSearchParams.get('assignmentID');
            }

            if (urlSearchParams.get('actionID')) {
                this.pegaResumeActionID = urlSearchParams.get('actionID');
            }

            if (urlSearchParams.get('initWithSubmit')) {
                this.pegaResumeInitWithSubmit = urlSearchParams.get('initWithSubmit') === 'true';
            }
        }
    }

    private async setCaseTypeId(forceIsBusinessParam: boolean = null): Promise<void> {
        if (this.pegaCaseTypeIdBusiness) {
            if (forceIsBusinessParam === null) {
                this.caseTypeID = (await this.pegaBusinessService.isBusinessCustomer()) ? this.pegaCaseTypeIdBusiness : this.pegaCaseTypeId;
                return;
            }

            if (forceIsBusinessParam) {
                this.caseTypeID = this.pegaCaseTypeIdBusiness;
                return;
            }
        }

        this.caseTypeID = this.pegaCaseTypeId;
    }

    private handleContentQueryParams(): void {
        if (this.apPegaSendDistributionChannelType || this.apPegaChannel === PegaChannelName.RETAIL) {
            this.pegaContent.DistributionChannelType = this.distributionChannelType;
        }

        if (this.apPegaContentQueryParams.length > 0) {
            const urlSearchParams = this.getURLSearchParams();
            for (const param of this.apPegaContentQueryParams) {
                if (urlSearchParams.get(param)) {
                    this.pegaContent[param] = urlSearchParams.get(param);
                }
            }
        }
    }
}
